import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "@/components/site/Seo";

export default function Privacy() {
  return (
    <>
      <Seo
        title="Privacy Policy | ProCraft Solutions"
        description="How ProCraft Solutions collects, uses, and protects your personal information — including SMS text messaging consent."
      />
      <div className="bg-paper min-h-screen" data-testid="privacy-page">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-24">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#16a34a] hover:text-[#15803d] transition mb-8"
            data-testid="privacy-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#16a34a]">
            Legal
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-[#1c1917] mt-2 tracking-tight">
            Privacy Policy
          </h1>
          <p className="mt-3 text-sm text-[#57534e] font-medium">
            Effective Date: September 22, 2026
          </p>

          <div className="mt-10 space-y-8 text-[#1c1917] leading-relaxed text-[15px]">
            <p>
              ProCraft Solutions respects your privacy and is committed to
              protecting the personal information you provide to us.
            </p>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3 text-[#1c1917]">
                Information We Collect
              </h2>
              <p className="mb-3">
                We may collect information you voluntarily provide when
                requesting an estimate, scheduling service, communicating with
                us, making a payment, or otherwise using our services. This
                information may include:
              </p>
              <ul className="list-disc pl-5 space-y-1.5 text-[#292524]">
                <li>Name</li>
                <li>Address and service location</li>
                <li>Phone number</li>
                <li>Email address</li>
                <li>Information regarding requested or completed services</li>
                <li>Appointment and service history</li>
                <li>Communications between you and ProCraft Solutions</li>
                <li>Billing and payment-related information</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3 text-[#1c1917]">
                How We Use Your Information
              </h2>
              <p className="mb-3">We may use your information to:</p>
              <ul className="list-disc pl-5 space-y-1.5 text-[#292524]">
                <li>Respond to inquiries and estimate requests</li>
                <li>Schedule and provide services</li>
                <li>
                  Communicate regarding appointments, estimates, jobs, invoices,
                  and payments
                </li>
                <li>Provide customer support</li>
                <li>Send service reminders and updates</li>
                <li>Maintain business and customer records</li>
                <li>Improve our services and customer experience</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3 text-[#1c1917]">
                Text Messaging
              </h2>
              <p className="mb-3">
                If you provide your mobile phone number and consent to receive
                text messages from ProCraft Solutions, we may send text messages
                regarding estimates, appointments, scheduling, service updates,
                invoices, payments, customer support, and other communications
                related to our services.
              </p>
              <p className="mb-3">
                Message frequency varies. Message and data rates may apply.
              </p>
              <p className="mb-3">
                You may opt out of text messages at any time by replying{" "}
                <strong>STOP</strong>. For assistance, reply{" "}
                <strong>HELP</strong> or contact ProCraft Solutions directly.
              </p>
              <p>
                SMS consent and mobile phone information will not be sold or
                shared with third parties or affiliates for their marketing or
                promotional purposes.
              </p>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3 text-[#1c1917]">
                Sharing of Information
              </h2>
              <p className="mb-3">
                ProCraft Solutions does not sell your personal information.
              </p>
              <p className="mb-3">
                We may share information with service providers that assist us
                in operating our business, such as scheduling and
                customer-management platforms, payment processors, communication
                providers, and other vendors necessary to provide our services.
              </p>
              <p>
                Information may also be disclosed when required by law or when
                reasonably necessary to protect our business, customers, or
                legal rights.
              </p>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3 text-[#1c1917]">
                Data Security
              </h2>
              <p>
                We take reasonable administrative and technical measures to
                safeguard personal information. However, no electronic
                transmission or storage system can be guaranteed to be
                completely secure.
              </p>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3 text-[#1c1917]">
                Your Choices
              </h2>
              <p className="mb-3">
                You may contact us to request that we update, correct, or delete
                personal information we maintain about you, subject to
                applicable legal and business recordkeeping requirements.
              </p>
              <p>
                You may opt out of SMS communications at any time by replying{" "}
                <strong>STOP</strong>.
              </p>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3 text-[#1c1917]">
                Changes to This Privacy Policy
              </h2>
              <p>
                We may update this Privacy Policy periodically. Updates will be
                posted on this page with a revised effective date.
              </p>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3 text-[#1c1917]">
                Contact Us
              </h2>
              <p className="mb-3">
                If you have questions regarding this Privacy Policy or how
                ProCraft Solutions handles your information, please contact us
                through our website:
              </p>
              <div className="rounded-2xl border border-[#e7e5df] bg-white p-5">
                <div className="font-display font-bold text-lg text-[#1c1917]">
                  ProCraft Solutions
                </div>
                <a
                  href="https://www.myprocraft.com"
                  className="text-[#16a34a] hover:underline text-sm font-semibold"
                >
                  www.MyProCraft.com
                </a>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
